import { set as setDB, get as getDB } from "idb-keyval";
import { ASSETS_URI, ECOLLAT_CACHE, HOST, PDF_DB } from "../config";
import { getAllPdf } from "./index";
import Service from "./service";
import { PwaAnalatics } from "../Functions/analytic";

// localStorage key holding a { [pdfID]: pdfVersion } map used to decide
// whether the images cached for a PDF are still valid.
const PDF_IMG_VERSIONS = "pdf_img_versions";

const User = window.__E_TT_Client;
const client = User.companySlug;
const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

let progressBar;
let timeout;
let processCount = 0;

const updateProgressBar = (barCount, removeBar = false, showError = false) => {
  if (!progressBar) {
    const root = document.getElementsByTagName("body")[0];

    const loadingBar = document.createElement("div");
    loadingBar.classList.add("pwa-progressbar");

    const fillBar = document.createElement("span");
    fillBar.classList.add("pwa-install-progress");
    loadingBar.appendChild(fillBar);

    progressBar = {
      bar: loadingBar,
      progress: fillBar,
    };

    root.appendChild(loadingBar);
  }

  if (progressBar.progress) {
    progressBar.progress.style.width = `${barCount}%`;
  }

  if (showError && progressBar.progress) {
    progressBar.progress.classList.add("pwa-error");
  }

  if (removeBar) {
    timeout = setTimeout(() => {
      clearTimeout(timeout);
      if (progressBar.bar) {
        progressBar.bar.remove();
        processCount = 0;
      }
    }, 2000);
  }
};
// Function to make a POST request
async function makePostRequest(url, data) {
  try {
    const response = Service.post(url, data, {
      headers: {
        "Content-Type": "application/json",
        // Add any other headers if needed
      },
    });

    // Axios automatically throws an error for non-2xx responses
    const jsonResponse = response.data;
    return jsonResponse;
  } catch (error) {
    console.error("Error making POST request:", error);
    throw error;
  }
}
export const getAllImagesUri = (pdfs) => {
  const size = window.innerWidth > 768 ? 0 : 1;

  if (pdfs && pdfs.length > 0) {
    let imgToCache = [];

    imgToCache.push(`${HOST}/allpagesforimages/pages/${window.__E_TT_Client.companySlug}`);
    pdfs.map(async (pdf) => {
      for (let i = 1; i <= pdf.totalPages; i++) {
        imgToCache.push(
          `${ASSETS_URI}/${window.__E_TT_Client.companySlug}/${pdf.pdfID}/${i}/image-${size}.jpg`
        );
        // imgToCache.push(`${HOST}/view-image.dll?id=${pdf.pdfID}&count=${i}&imagesize=${size}`);
      }
      // imgToCache.push(response);
      // imgToCache.push(`${HOST}/searchaccordingtoslug/${pdf.pdfSlug}`);
      // imgToCache.push(`${HOST}/${client}/${pdf.pdfID}/Links/menu.json`);
      // imgToCache.push(`${HOST}/getlinks/${pdf.pdfID}`);
      imgToCache.push(pdf.pdfcoverPath);
      return pdf;
    });
    return imgToCache;
  }
  return [];
};

// Build the exact image URIs the viewer will request for a single PDF, so the
// cache keys match the <img> requests (see getImage() in Functions/viewer.js).
const getPdfImageUris = (pdf) => {
  if (!pdf || !pdf.pdfID || !pdf.totalPages) return [];

  const size = window.innerWidth > 768 ? 0 : 1;
  const companyName =
    (pdf.coverImage && pdf.coverImage.split("/")[0]) ||
    window.__E_TT_Client.companySlug;

  const uris = [];
  for (let i = 1; i <= pdf.totalPages; i++) {
    let page = i;
    if (pdf.parent && pdf.pdfSlug && pdf.pages && pdf.pages[i - 1]) {
      page = parseInt(pdf.pages[i - 1]);
    }
    uris.push(
      `${ASSETS_URI}/${companyName}/${pdf.pdfID}/${page}/image-${size}.jpg`
    );
  }
  if (pdf.coverImage) {
    uris.push(`${ASSETS_URI}/${pdf.coverImage}`);
  }
  return uris;
};

const readPdfImgVersions = () => {
  try {
    return JSON.parse(localStorage.getItem(PDF_IMG_VERSIONS) || "{}") || {};
  } catch (e) {
    return {};
  }
};

// Fetch every image of a PDF and store it in the Cache Storage bucket used by
// the service worker. Runs in the background, best-effort.
const putPdfImagesInCache = async (uris) => {
  try {
    const cache = await caches.open(ECOLLAT_CACHE);
    await Promise.all(
      uris.map(async (url) => {
        try {
          const req = new Request(url);
          const res = await fetch(req);
          if (res && (res.ok || res.type === "opaque")) {
            await cache.put(req, res.clone());
          }
        } catch (e) {
          // ignore a single image failure
        }
      })
    );
  } catch (e) {
    // caching is best-effort
  }
};

/**
 * Version-aware image cache for a single PDF (used by the slug viewer).
 *
 * - Stores the PDF's version in localStorage keyed by pdfID.
 * - If the stored version matches the current one AND the images are already
 *   cached, the existing cache is reused (no re-download).
 * - If the version changed (or nothing is cached yet), the old images for that
 *   PDF are removed from the cache and re-downloaded with the new version.
 *
 * The version-invalidation (removing stale entries) is awaited so that, on a
 * mismatch, the very next <img> request misses the cache and loads fresh
 * images; the re-download runs in the background.
 */
export const cachePdfImagesByVersion = async (pdf) => {
  try {
    if (!pdf || !pdf.pdfID || typeof caches === "undefined") return;

    const uris = getPdfImageUris(pdf);
    if (uris.length === 0) return;

    const versions = readPdfImgVersions();
    const pdfKey = String(pdf.pdfID);
    const currentVersion = pdf.pdfVersion;
    const storedVersion = versions[pdfKey];

    const cache = await caches.open(ECOLLAT_CACHE);

    const versionMatches =
      storedVersion !== undefined &&
      String(storedVersion) === String(currentVersion);

    if (versionMatches) {
      // Version unchanged — if the images are already cached, use them as-is.
      const cached = await cache.match(new Request(uris[0]));
      if (cached) return;
    } else {
      // Version changed (or first visit) — drop this PDF's stale images.
      await Promise.all(uris.map((url) => cache.delete(new Request(url))));
    }

    // Re-download images in the background and record the current version.
    versions[pdfKey] = currentVersion;
    try {
      localStorage.setItem(PDF_IMG_VERSIONS, JSON.stringify(versions));
    } catch (e) {}

    putPdfImagesInCache(uris);
  } catch (e) {
    // caching must never break the viewer
  }
};

export const updatePdfsOffline = async (pdfs) => {
  try {
    let oldPdf = await getDB(PDF_DB).then((res) => res && JSON.parse(res));

    oldPdf = oldPdf && oldPdf.length > 0 ? oldPdf.map((p) => p.pdfID) : [];
    let pdfToOffline = pdfs && pdfs.length > 0 ? pdfs : [];

    if (oldPdf && oldPdf.length > 0 && pdfs && pdfs.length > 0) {
      pdfToOffline = pdfs.filter((p) => !oldPdf.includes(p.pdfID));
    }

    const imageToCache = getAllImagesUri(pdfToOffline);
    await cacheImages(imageToCache);

    if (pdfs) {
      setDB(PDF_DB, JSON.stringify(pdfs));
    }
  } catch (e) {
  }
};

export const PedfVersionRemoveCache = async (pdfs) => {
  try {
    let oldPdf = await getDB(PDF_DB).then((res) => res && JSON.parse(res));

    oldPdf = oldPdf && oldPdf.length > 0 ? oldPdf.map((p) => p) : [];
    let pdfToOffline = pdfs && pdfs.length > 0 ? pdfs : [];
    // if (oldPdf && oldPdf.length > 0 && pdfs && pdfs.length > 0) {
    //   pdfToOffline = pdfs.filter((p) => !oldPdf.includes(p.pdfID));
    // }
    var pdfall = []
    pdfs.map(async item => {
      const pdf = oldPdf.filter(p => p.pdfID === item.pdfID)

      if (pdf.length > 0) {
        if (pdf[0].pdfVersion !== item.pdfVersion) {
          pdfall.push(item)
        }
      } else {
        pdfall.push(item)
      }

    })




    const imageToCache = getAllImagesUri(pdfall);
    await RemovecacheImages(imageToCache);

    if (pdfs) {
      setDB(PDF_DB, JSON.stringify(pdfs));
    }
  } catch (e) {
  }
};
export const RemovecacheImages = async (imageUris = false, savePdfData = false) => {
  let pdfs;
  if (!imageUris) {
    pdfs = await getAllPdf(client, false);
    imageUris = getAllImagesUri(pdfs, client);
  }

  if (imageUris.length === 0) return true;

  try {
    const cache = await caches.open(ECOLLAT_CACHE).catch((e) => false);

    if (cache && imageUris) {
      let syncStatus = await Promise.all(
        imageUris.map(async (url) => {
          let req = new Request(url);
          let cacheResponse = await caches.match(req);

          if (cacheResponse) {
            processCount++;
            await cache.delete(req);
            return true;
          }
          return false;
        })
      );
      if (syncStatus.filter((e) => !e).length > 7) {
        // throw new Error("Few errors found while syncing");
      }
    }

    if (pdfs && savePdfData) {
      setDB(PDF_DB, JSON.stringify(pdfs));
    }

    return true;
  } catch (e) {
    // alert("An error has occurred, please reinstall the app.");
    return e;
  }
};


export const cacheImages = async (imageUris = false, savePdfData = false) => {
  let pdfs;
  if (!imageUris) {
    pdfs = await getAllPdf(client, false);
    imageUris = getAllImagesUri(pdfs, client);
  }

  if (imageUris.length === 0) return true;

  try {
    const cache = await caches.open(ECOLLAT_CACHE).catch((e) => false);

    if (cache && imageUris) {
      let syncStatus = await Promise.all(
        imageUris.map(async (url) => {
          let req = new Request(url);
          let res = await fetch(req).catch((e) => {
            return false;
          });

          if (res) {
            processCount++;
            cache.put(req, res);
            updateProgressBar(processCount);
            return true;
          }
          return false;
        })
      );

      if (syncStatus.filter((e) => !e).length > 7) {
        throw new Error("Few errors found while syncing");
      }
    }

    if (pdfs && savePdfData) {
      setDB(PDF_DB, JSON.stringify(pdfs));
    }

    updateProgressBar(100, true);
    if (iOS) {
      alert("Successfully synced.");
    } else {
      alert("Successfully synced.");
    }
    return true;
  } catch (e) {
    updateProgressBar(processCount, true, true);
    alert("An error has occurred, please reinstall the app.");
    return e;
  }
};

window.addEventListener("appinstalled", (event) => {



  PwaAnalatics({
    companySlug: window.__E_TT_Client.companySlug,
    isMobile: window.innerWidth > 768 ? false : true
  })






  cacheImages(false, true).then((res) => {
  });
});
